import {createContext, memo, useCallback, use, useMemo, useReducer, useState} from 'react'
import {Button, FormControl, Textarea} from '@primer/react'
import {TriangleDownIcon} from '@primer/octicons-react'
import {ModelPicker} from '@github-ui/copilot-fix-controls/ModelPicker'
import {SplitButton, type SplitButtonItem} from '@github-ui/copilot-fix-controls/SplitButton'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {ScopedCommands} from '@github-ui/ui-commands'
import {useUnifiedBatch} from '../../../stores/layout-selectors'
import {useManageBatchTelemetry} from './manage-batch-telemetry'
import styles from './ManageBatchFixTab.module.css'

/**
 * The two CCA hand-off modes surfaced by the Fix with Copilot tab's primary
 * CTA. `commit` pushes a Copilot-authored commit directly to the PR branch;
 * `pull_request` opens a new PR with the changes. Mirrors `APPLY_MODE_ITEMS`
 * in `CopilotFixDialog.tsx`.
 */
export type ManageBatchFixApplyMode = 'commit' | 'pull_request'

export const APPLY_MODE_ITEMS: Array<SplitButtonItem<ManageBatchFixApplyMode>> = [
  {
    value: 'commit',
    label: 'Fix and commit',
    description: "Copilot will push a commit directly to this pull request's branch.",
  },
  {
    value: 'pull_request',
    label: 'Fix and open pull request',
    description: 'Copilot will create a new pull request with the changes.',
  },
]

const BASE_ERROR_MESSAGE = 'There was an error sending the batch to Copilot'
const FALLBACK_VIEWER_HANDLE = 'You'

type FormState = {
  additionalInstructions: string
  selectedModel: string
  applyMode: ManageBatchFixApplyMode
}

type FormAction =
  | {type: 'CHANGE_INSTRUCTIONS'; payload: string}
  | {type: 'CHANGE_MODEL'; payload: string}
  | {type: 'CHANGE_APPLY_MODE'; payload: ManageBatchFixApplyMode}

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'CHANGE_INSTRUCTIONS':
      return {...state, additionalInstructions: action.payload}
    case 'CHANGE_MODEL':
      return {...state, selectedModel: action.payload}
    case 'CHANGE_APPLY_MODE':
      return {...state, applyMode: action.payload}
  }
}

/**
 * Shared state between the Fix tab body (description + textarea + model picker)
 * and the footer primary CTA (`Fix and commit ▾` split button). Like the
 * Commit tab, these slots render in different places in `ManageBatchPanel`,
 * so the form values, submission status, and error message live in a context
 * provider that wraps both slots.
 */
type ManageBatchFixContextValue = {
  formValues: FormState
  setInstructions: (value: string) => void
  setSelectedModel: (modelId: string) => void
  setApplyMode: (mode: ManageBatchFixApplyMode) => void
  isSubmitting: boolean
  errorMessage: string | undefined
  submit: () => void
  canSubmit: boolean
  viewerHandle: string
  modelsUrl: string | undefined
}

const ManageBatchFixContext = createContext<ManageBatchFixContextValue | undefined>(undefined)

function useManageBatchFixContext(): ManageBatchFixContextValue {
  const ctx = use(ManageBatchFixContext)
  if (!ctx) {
    throw new Error('ManageBatchFix components must be rendered inside <ManageBatchFixProvider>.')
  }
  return ctx
}

/**
 * Binds the `github:submit-form` command (⌘/Ctrl+Enter) to the Fix tab's
 * submit handler for its entire subtree, mirroring how `ApplySuggestionDialog`
 * wraps its form in `ScopedCommands` so the batch can be handed off to Copilot
 * via the keyboard while focus is anywhere in the form area.
 *
 * `ScopedCommands` renders a `display: contents` wrapper when no `className`
 * or `as` is supplied, so this does not affect the panel's flex layout.
 */
export const ManageBatchFixFormCommands = memo(function ManageBatchFixFormCommandsImpl({
  children,
}: {
  children: React.ReactNode
}) {
  const {submit} = useManageBatchFixContext()
  return <ScopedCommands commands={{'github:submit-form': submit}}>{children}</ScopedCommands>
})

/**
 * Provider for the Fix with Copilot tab.
 *
 * Wraps both the body slot (`ManageBatchFixTab`) and the footer primary CTA
 * slot (`ManageBatchFixTabPrimaryCta`) so they can share form state and the
 * submit callback without prop drilling through `ManageBatchPanel`.
 *
 * The submit handler is intentionally a stub that emits telemetry and resolves
 * synchronously. Wiring the real CCA hand-off path requires:
 *  - Adapting `useFixWithCopilot` to accept multiple `review_references` (one
 *    per batched comment) instead of a single comment permalink.
 *  - Mapping each `UnifiedBatchItem` to its comment permalink — the
 *    `UnifiedBatchItem` only carries identifiers today.
 *  - Sourcing a real `modelsUrl` from the route payload.
 *
 * That backend wiring is the responsibility of the Fix with Copilot data-layer
 * follow-up (#24205) and is intentionally out of scope here.
 */
export const ManageBatchFixProvider = memo(function ManageBatchFixProviderImpl({
  children,
  onSubmitted,
  viewerLogin,
  modelsUrl,
}: {
  children: React.ReactNode
  /** Called after a successful submission so the panel can close itself. */
  onSubmitted?: () => void
  /**
   * Override for the templated handle. Tests and Storybook pass this to keep
   * snapshots deterministic; production reads it via `getCurrentUserLogin()`.
   */
  viewerLogin?: string
  /**
   * Endpoint the `ModelPicker` fetches model options from. When omitted (e.g.
   * before the data-layer follow-up (#24205) plumbs it through
   * `FilesRoutePayload`), the picker silently falls back to a single "Auto"
   * option, matching the prototype's `Auto ▾` pill.
   */
  modelsUrl?: string
}) {
  const {items} = useUnifiedBatch()
  const {onSubmit, onSubmitSuccess, onSubmitFailure} = useManageBatchTelemetry()
  const [formValues, dispatch] = useReducer(formReducer, {
    additionalInstructions: '',
    selectedModel: 'auto',
    applyMode: 'commit',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)

  const setInstructions = useCallback((value: string) => dispatch({type: 'CHANGE_INSTRUCTIONS', payload: value}), [])
  const setSelectedModel = useCallback((value: string) => dispatch({type: 'CHANGE_MODEL', payload: value}), [])
  const setApplyMode = useCallback(
    (value: ManageBatchFixApplyMode) => dispatch({type: 'CHANGE_APPLY_MODE', payload: value}),
    [],
  )

  const resolvedViewer = viewerLogin ?? getCurrentUserLogin() ?? FALLBACK_VIEWER_HANDLE

  // Unlike the Commit tab, the Fix tab is always valid as long as the batch
  // has items — there is no required input. The Fix path supports both
  // suggestion-bearing and human-comment-only items, which is what makes it
  // the fallback when the Commit tab's "all items must have suggestions"
  // precondition fails.
  const canSubmit = items.length > 0 && !isSubmitting

  const submit = useCallback(() => {
    if (!canSubmit) return

    onSubmit('fix')
    setIsSubmitting(true)
    setErrorMessage(undefined)

    // Stubbed hand-off: emit telemetry and resolve synchronously. The real CCA
    // path is wired by the Fix with Copilot data-layer follow-up (#24205) once
    // items carry the comment permalinks.
    try {
      onSubmitSuccess('fix')
      setIsSubmitting(false)
      onSubmitted?.()
    } catch (error) {
      onSubmitFailure('fix', error)
      setIsSubmitting(false)
      const friendly = error instanceof Error ? error.message : undefined
      setErrorMessage(friendly ? `${BASE_ERROR_MESSAGE}: ${friendly}` : `${BASE_ERROR_MESSAGE}.`)
    }
  }, [canSubmit, onSubmit, onSubmitSuccess, onSubmitFailure, onSubmitted])

  const value = useMemo<ManageBatchFixContextValue>(
    () => ({
      formValues,
      setInstructions,
      setSelectedModel,
      setApplyMode,
      isSubmitting,
      errorMessage,
      submit,
      canSubmit,
      viewerHandle: resolvedViewer,
      modelsUrl,
    }),
    [
      formValues,
      setInstructions,
      setSelectedModel,
      setApplyMode,
      isSubmitting,
      errorMessage,
      submit,
      canSubmit,
      resolvedViewer,
      modelsUrl,
    ],
  )

  return <ManageBatchFixContext value={value}>{children}</ManageBatchFixContext>
})

/**
 * Body content for the Fix with Copilot tab.
 *
 * Renders the templated description sentence — *"Ask Copilot cloud agent to
 * address the comments in this batch. **{handle}** will be added as
 * co-author on any new commits."* — followed by the
 * `Add additional instructions for Copilot...` textarea with the `Auto ▾`
 * model picker pill inline in its toolbar.
 *
 * The textarea + toolbar container mirrors the styling lifted from
 * `CopilotFixDialog.module.css` (`.textareaContainer` / `.textareaToolbar`)
 * so the unified panel feels visually consistent with the per-comment Fix
 * with Copilot dialog.
 */
export const ManageBatchFixTab = memo(function ManageBatchFixTabImpl() {
  const {formValues, setInstructions, setSelectedModel, errorMessage, viewerHandle, modelsUrl, isSubmitting} =
    useManageBatchFixContext()

  return (
    <div className={styles.body} data-testid="manage-batch-fix-tab">
      <p className={styles.description}>
        Ask Copilot cloud agent to address the comments in this batch.{' '}
        <span className={styles.descriptionHandle} data-testid="manage-batch-fix-viewer-handle">
          {viewerHandle}
        </span>{' '}
        will be added as co-author on any new commits.
      </p>
      <div className={styles.textareaContainer}>
        <Textarea
          className={styles.instructionsTextarea}
          aria-label="Additional instructions for Copilot"
          placeholder="Add additional instructions for Copilot..."
          value={formValues.additionalInstructions}
          onChange={event => setInstructions(event.target.value)}
          rows={4}
          resize="none"
          disabled={isSubmitting}
          data-testid="manage-batch-fix-instructions-input"
        />
        <div className={styles.textareaToolbar}>
          {modelsUrl ? (
            <ModelPicker modelsUrl={modelsUrl} onModelChange={setSelectedModel} />
          ) : (
            <Button
              variant="invisible"
              size="small"
              trailingVisual={TriangleDownIcon}
              disabled
              data-testid="manage-batch-fix-model-picker-fallback"
              aria-label="Model: Auto"
            >
              Auto
            </Button>
          )}
        </div>
      </div>
      {errorMessage && (
        // Wrapped in a `role="alert"` live region so screen readers announce
        // the submission error when it appears. `FormControl.Validation`
        // alone is not programmatically associated with any input here
        // (it is rendered outside a `FormControl`), so it would not be
        // announced without an explicit live region. See WCAG 4.1.3.
        <div role="alert">
          <FormControl.Validation variant="error" className={styles.errorRow} data-testid="manage-batch-fix-error">
            {errorMessage}
          </FormControl.Validation>
        </div>
      )}
    </div>
  )
})

/**
 * Primary CTA for the Fix with Copilot tab footer slot.
 *
 * Renders the green `Fix and commit ▾` split button; disabled when the batch
 * is empty or while a submission is in flight. The split caret reveals
 * `Fix and open pull request` as a secondary action. Lifts the
 * `SplitButton` + `APPLY_MODE_ITEMS` pattern from `CopilotFixDialog.tsx`.
 */
export const ManageBatchFixTabPrimaryCta = memo(function ManageBatchFixTabPrimaryCtaImpl() {
  const {
    submit,
    canSubmit,
    isSubmitting,
    formValues: {applyMode},
    setApplyMode,
  } = useManageBatchFixContext()

  return (
    <div data-testid="manage-batch-fix-cta">
      <SplitButton
        items={APPLY_MODE_ITEMS}
        selectedValue={applyMode}
        onSelectionChange={setApplyMode}
        onAction={submit}
        variant="primary"
        disabled={!canSubmit}
        loading={isSubmitting}
        dropdownAriaLabel="Change Fix with Copilot action"
      />
    </div>
  )
})
